import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Owns custom-template CRUD: list reload, create, edit, delete.
// `onTemplatesReloaded` is called after every successful save/delete so the
// EHR push hook can re-fetch its template catalog.
export function useCustomTemplates({
  token,
  selectedTemplateKey,
  onSelectTemplate,
  onTemplatesReloaded,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [labelsText, setLabelsText] = useState('');
  const [guidelines, setGuidelines] = useState({});
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setLabelsText('');
    setGuidelines({});
    setModalOpen(true);
  };

  const openEdit = async (customKey) => {
    const id = customKey.startsWith('custom:') ? customKey.slice(7) : customKey;
    try {
      const res = await axios.get(`${API}/ehr/custom-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tpl = (res.data?.templates || []).find((t) => t.id === id);
      if (!tpl) {
        toast.error('Custom template not found');
        return;
      }
      setEditingId(id);
      setName(tpl.name || '');
      setLabelsText((tpl.labels || []).join('\n'));
      setGuidelines(tpl.guidelines || {});
      setModalOpen(true);
    } catch {
      toast.error('Could not load custom template');
    }
  };

  const save = async () => {
    const cleanName = name.trim();
    const labels = labelsText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!cleanName) { toast.error('Give your template a name'); return; }
    if (!labels.length) { toast.error('Add at least one section label'); return; }
    const cleanGuidelines = {};
    labels.forEach((l) => { if (guidelines[l]) cleanGuidelines[l] = guidelines[l]; });
    setSaving(true);
    try {
      const payload = { name: cleanName, labels, guidelines: cleanGuidelines };
      let saved;
      if (editingId) {
        const res = await axios.patch(`${API}/ehr/custom-templates/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        saved = res.data;
        toast.success(`Updated "${saved?.name || cleanName}"`);
      } else {
        const res = await axios.post(`${API}/ehr/custom-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        saved = res.data;
        toast.success(`Created "${saved?.name || cleanName}"`);
      }
      setModalOpen(false);
      await onTemplatesReloaded?.();
      if (saved?.id) onSelectTemplate?.(`custom:${saved.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save custom template');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tplIdArg) => {
    const tplId = tplIdArg || editingId;
    if (!tplId) return;
    if (!window.confirm('Delete this custom template? This cannot be undone.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/ehr/custom-templates/${tplId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Custom template deleted');
      if (selectedTemplateKey === `custom:${tplId}`) onSelectTemplate?.('');
      setModalOpen(false);
      await onTemplatesReloaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete custom template');
    } finally {
      setSaving(false);
    }
  };

  return {
    modalOpen, setModalOpen,
    editingId,
    name, setName,
    labelsText, setLabelsText,
    guidelines, setGuidelines,
    saving,
    openCreate,
    openEdit,
    save,
    remove,
  };
}

// Custom-EHR catalog (owner-defined Enterprise note formats). Loaded once.
export function useCustomEhrs(token) {
  const [customEhrs, setCustomEhrs] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/ehr/extension/custom-ehrs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCustomEhrs(res.data?.custom_ehrs || []))
      .catch(() => {});
  }, [token]);
  return customEhrs;
}
